import { useEffect, useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import type { TAdminUser, TIssuedAccessLink } from '@schemas'

import {
  useAdminUserDetail,
  useDeleteAdminUser,
  useIssueAccessLink,
  useSetAdminUserDisabled,
  useSetAdminUserPerson,
  useSignOutAdminUser,
  useUpdateAdminUser,
} from '@/api/hooks'
import { parseApiErrorMessage } from '@/lib/api-errors'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  AccessLinkPanel,
  AccountStatusBadge,
  ConfirmDialog,
  formatDate,
  PersonPicker,
  type PickedPerson,
} from './account-components'
import { AccountHistorySection, ShowAccessSection } from './account-access-sections'

type Confirmation = 'disable' | 'sign-out' | 'delete' | 'revoke-admin'

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2" aria-label={title}>
      <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
      {children}
    </section>
  )
}

/**
 * One account's sign-in state, identity and administration actions. `onChanged` receives the
 * account each change returns, so the sheet stays current even when the change moves the
 * account out of the list behind it.
 */
export function UserAccountSheet({
  user,
  isSelf,
  onChanged,
  onClose,
}: {
  user: TAdminUser
  isSelf: boolean
  onChanged: (user: TAdminUser) => void
  onClose: () => void
}) {
  const updateUser = useUpdateAdminUser()
  const setDisabled = useSetAdminUserDisabled()
  const issueLink = useIssueAccessLink()
  const signOut = useSignOutAdminUser()
  const deleteUser = useDeleteAdminUser()
  const detail = useAdminUserDetail(user.id)
  const [email, setEmail] = useState(user.email)
  const [link, setLink] = useState<TIssuedAccessLink | null>(null)
  const [confirming, setConfirming] = useState<Confirmation | null>(null)
  const [linkingPerson, setLinkingPerson] = useState(false)

  // A saved email resets the form.
  useEffect(() => setEmail(user.email), [user.email])

  // Only the account's current link is shown: a newer link, an email change or disabling the
  // account ends it on the server, and its expiry then no longer matches.
  const liveLink = link && user.accessLinkExpiresAt === link.expiresAt ? link : null

  /** Runs one change; `success` is toasted unless the sheet itself shows the result. */
  const run = async (work: () => Promise<unknown>, success?: string) => {
    try {
      await work()
      if (success) toast.success(success)
      return true
    } catch (error) {
      toast.error(parseApiErrorMessage(error))
      return false
    } finally {
      setConfirming(null)
    }
  }

  const changeUser = async (change: Promise<TAdminUser>) => onChanged(await change)

  const unusedInvitation = !user.hasPassword && !user.lastLoginAt
  const emailChanged = email.trim().toLowerCase() !== user.email.toLowerCase()

  const confirmations: Record<
    Confirmation,
    { title: string; label: string; body: ReactNode; pending: boolean; confirm: () => unknown }
  > = {
    disable: {
      title: 'Disable account',
      label: 'Disable account',
      body: `${user.email} is signed out everywhere and cannot sign in until an administrator enables the account again.`,
      pending: setDisabled.isPending,
      confirm: () =>
        run(
          () => changeUser(setDisabled.mutateAsync({ userId: user.id, disabled: true })),
          'Account disabled.'
        ),
    },
    'sign-out': {
      title: 'Sign out all devices',
      label: 'Sign out',
      body: isSelf
        ? 'Every open session of your account ends, including this one: you will need to sign in again.'
        : `Every open session of ${user.email} ends; they can sign in again with their password.`,
      pending: signOut.isPending,
      confirm: () => run(() => signOut.mutateAsync(user.id), 'Signed out everywhere.'),
    },
    delete: {
      title: 'Delete invitation',
      label: 'Delete invitation',
      body: `The account for ${user.email} is removed and its setup link stops working.`,
      pending: deleteUser.isPending,
      confirm: async () => {
        if (await run(() => deleteUser.mutateAsync(user.id), 'Invitation deleted.')) onClose()
      },
    },
    'revoke-admin': {
      title: 'Remove administrator access',
      label: 'Remove access',
      body: `${user.email} keeps only the show roles of their linked person.`,
      pending: updateUser.isPending,
      confirm: () =>
        run(
          () =>
            changeUser(updateUser.mutateAsync({ userId: user.id, payload: { isAdmin: false } })),
          'Administrator access removed.'
        ),
    },
  }
  const pendingConfirmation = confirming ? confirmations[confirming] : null

  return (
    <Sheet open onOpenChange={open => !open && onClose()}>
      <SheetContent className="w-full sm:max-w-xl" data-testid="user-account-sheet">
        <SheetHeader className="space-y-1">
          <SheetTitle className="break-all">{user.email}</SheetTitle>
          <SheetDescription>
            {user.person ? `Linked to ${user.person.name}. ` : 'Not linked to a person. '}
            Created {formatDate(user.createdAt)}; last sign-in {formatDate(user.lastLoginAt)}.
          </SheetDescription>
          <div className="flex flex-wrap gap-2">
            <AccountStatusBadge user={user} />
            {user.isAdmin ? <Badge variant="secondary">Administrator</Badge> : null}
          </div>
        </SheetHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 pb-6">
          <Section title="Sign-in">
            {user.status === 'disabled' ? (
              <p className="text-sm">
                This account is disabled: it cannot sign in and has no open sessions.
              </p>
            ) : (
              <>
                <p className="text-sm">
                  {user.status === 'invited'
                    ? 'This person has not set a password yet. Give them a setup link.'
                    : 'Give this person a reset link if they cannot sign in. Their password stays the same until they use it.'}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  disabled={issueLink.isPending}
                  onClick={() =>
                    run(async () => {
                      const issued = await issueLink.mutateAsync(user.id)
                      setLink(issued.accessLink)
                      onChanged(issued.user)
                    })
                  }
                >
                  {user.status === 'invited' ? 'Create setup link' : 'Create password reset link'}
                </Button>
                {liveLink ? (
                  <AccessLinkPanel link={liveLink} userId={user.id} email={user.email} />
                ) : null}
              </>
            )}
            <div className="flex flex-wrap gap-2">
              {user.status === 'disabled' ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={setDisabled.isPending}
                  onClick={() =>
                    run(
                      () =>
                        changeUser(setDisabled.mutateAsync({ userId: user.id, disabled: false })),
                      'Account enabled.'
                    )
                  }
                >
                  Enable account
                </Button>
              ) : (
                <>
                  <Button type="button" variant="outline" onClick={() => setConfirming('sign-out')}>
                    Sign out all devices
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isSelf}
                    onClick={() => setConfirming('disable')}
                  >
                    Disable account
                  </Button>
                </>
              )}
              {unusedInvitation ? (
                <Button type="button" variant="outline" onClick={() => setConfirming('delete')}>
                  Delete invitation
                </Button>
              ) : null}
            </div>
            {isSelf ? (
              <p className="text-xs text-muted-foreground">
                You cannot disable your own account or remove your own administrator access. Ask
                another administrator.
              </p>
            ) : null}
          </Section>

          <Section title="Email">
            <form
              className="flex gap-2"
              onSubmit={event => {
                event.preventDefault()
                void run(
                  () =>
                    changeUser(
                      updateUser.mutateAsync({ userId: user.id, payload: { email: email.trim() } })
                    ),
                  'Email updated.'
                )
              }}
            >
              <Input
                aria-label="Account email"
                type="email"
                value={email}
                onChange={event => setEmail(event.target.value)}
              />
              <Button
                type="submit"
                variant="outline"
                disabled={!emailChanged || updateUser.isPending}
              >
                Save email
              </Button>
            </form>
            <p className="text-xs text-muted-foreground">
              Changing the email ends any outstanding setup or reset link.
            </p>
          </Section>

          <Section title="Administrator access">
            <p className="text-sm">
              {user.isAdmin
                ? 'Administrators can manage every show and every account.'
                : 'This account has only the show roles of its linked person.'}
            </p>
            {user.isAdmin ? (
              <Button
                type="button"
                variant="outline"
                disabled={isSelf || updateUser.isPending}
                onClick={() => setConfirming('revoke-admin')}
              >
                Remove administrator access
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                disabled={updateUser.isPending}
                onClick={() =>
                  run(
                    () =>
                      changeUser(
                        updateUser.mutateAsync({ userId: user.id, payload: { isAdmin: true } })
                      ),
                    'Administrator access granted.'
                  )
                }
              >
                Make administrator
              </Button>
            )}
          </Section>

          <Section title="Person">
            <p className="text-sm">
              {user.person
                ? `${user.person.name}${user.person.email ? ` (${user.person.email})` : ''}. The account uses this person's show assignments.`
                : 'Link a person to give this account their show roles.'}
            </p>
            <Button type="button" variant="outline" onClick={() => setLinkingPerson(true)}>
              {user.person ? 'Change person' : 'Link person'}
            </Button>
          </Section>

          <Section title="Show access">
            <ShowAccessSection
              user={user}
              access={detail.data?.showAccess ?? []}
              assignableShows={detail.data?.assignableShows ?? []}
              isLoading={detail.isLoading}
            />
          </Section>

          <Section title="History">
            <AccountHistorySection
              history={detail.data?.history ?? []}
              isLoading={detail.isLoading}
            />
          </Section>
        </div>

        {pendingConfirmation ? (
          <ConfirmDialog
            title={pendingConfirmation.title}
            confirmLabel={pendingConfirmation.label}
            pending={pendingConfirmation.pending}
            onCancel={() => setConfirming(null)}
            onConfirm={() => void pendingConfirmation.confirm()}
          >
            {pendingConfirmation.body}
          </ConfirmDialog>
        ) : null}
        {linkingPerson ? (
          <PersonLinkDialog
            user={user}
            onSaved={onChanged}
            onClose={() => setLinkingPerson(false)}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  )
}

function PersonLinkDialog({
  user,
  onSaved,
  onClose,
}: {
  user: TAdminUser
  onSaved: (user: TAdminUser) => void
  onClose: () => void
}) {
  const [person, setPerson] = useState<PickedPerson | null>(user.person)
  const mutation = useSetAdminUserPerson()
  const save = async () => {
    try {
      onSaved(await mutation.mutateAsync({ userId: user.id, personId: person?.id ?? null }))
      toast.success('Person link saved. The user must sign in again.')
      onClose()
    } catch (error) {
      toast.error(parseApiErrorMessage(error))
    }
  }
  return (
    <Dialog open onOpenChange={open => !open && !mutation.isPending && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Link account to person</DialogTitle>
          <DialogDescription>
            Choose the person whose show assignments apply to {user.email}. Saving signs this
            account out on all devices.
          </DialogDescription>
        </DialogHeader>
        <PersonPicker selected={person} onSelect={setPerson} />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={mutation.isPending || (person?.id ?? null) === user.personId}
          >
            {mutation.isPending ? 'Saving…' : 'Save person link'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
